// Modal pra revisar/editar/regenerar/remover questões dissertativas
// geradas por IA dentro de payload.questoesDissertativas.
//
// Aberto da DissertativeSection no EditBankPage (botão "Revisar").
// Cada questão é um card com: enunciado, gabarito (resposta-modelo +
// pontos esperados + critérios + armadilhas) + ações por item:
//   - Editar (form inline com textareas)
//   - Regenerar SÓ essa (IA refaz)
//   - Remover SÓ essa do array
// Confirma antes de mutações destrutivas.

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface DissertativeItem {
  originalQuestionIdx: number;
  tema: string;
  caso: string;
  imagens: unknown[];
  tabelas: unknown[];
  enunciado: string;
  gabarito: {
    resposta_modelo: string;
    pontos_esperados: Array<{ ponto: string; peso: number }>;
    criterios: string[];
    armadilhas_comuns: string[];
  };
}

interface ListResponse {
  bank: { id: string; name: string };
  mcCount: number;
  dissertativeCount: number;
  items: DissertativeItem[];
}

export function DissertativeReviewModal({
  bankId,
  bankName,
  onClose
}: {
  bankId: string;
  bankName: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [items, setItems] = useState<DissertativeItem[]>([]);
  const [mcCount, setMcCount] = useState(0);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function reload() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await call<ListResponse>(
        `/api/manager/banks/dissertative-item?id=${encodeURIComponent(bankId)}`
      );
      setItems(r.items);
      setMcCount(r.mcCount);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void reload();
  }, [bankId]);

  function updateLocal(idx: number, next: DissertativeItem) {
    setItems((prev) => {
      const arr = [...prev];
      arr[idx] = next;
      return arr;
    });
  }
  function removeLocal(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex shrink-0 items-center justify-between border-b border-border px-5 py-3.5">
          <div class="min-w-0 flex-1">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Sparkles width="1.2em" height="1.2em" className="text-fuchsia-600 dark:text-fuchsia-300" />
              Revisar versão dissertativa
            </h2>
            <p class="mt-0.5 truncate text-xs text-muted-foreground">
              {bankName} · {items.length} dissertativa{items.length !== 1 ? "s" : ""} ·{" "}
              {mcCount} MC original{mcCount !== 1 ? "is" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          {phase === "loading" && (
            <>
              <Skeleton class="h-32 w-full" />
              <Skeleton class="h-32 w-full" />
            </>
          )}
          {phase === "error" && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <AlertCircle className="mr-2 inline size-4" />
              {errMsg}
            </div>
          )}
          {phase === "ready" && items.length === 0 && (
            <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-center">
              <p class="text-sm text-muted-foreground">
                Nenhuma questão dissertativa neste banco.
              </p>
            </div>
          )}
          {phase === "ready" &&
            items.map((it, i) => (
              <DissertativeItemCard
                key={`${bankId}-${i}`}
                bankId={bankId}
                idx={i}
                item={it}
                hasMc={i < mcCount}
                onUpdated={(next) => updateLocal(i, next)}
                onRemoved={() => removeLocal(i)}
              />
            ))}
        </div>

        <div class="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-card px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function DissertativeItemCard({
  bankId,
  idx,
  item,
  hasMc,
  onUpdated,
  onRemoved
}: {
  bankId: string;
  idx: number;
  item: DissertativeItem;
  hasMc: boolean;
  onUpdated: (next: DissertativeItem) => void;
  onRemoved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  async function regenerate() {
    if (!hasMc) {
      alert("Esta questão não tem MC original correspondente — regenerar não funciona aqui.");
      return;
    }
    if (!confirm(`Regenerar a questão ${idx + 1} via IA? Sobrescreve enunciado e gabarito atuais.`)) return;
    setRegenerating(true);
    try {
      const r = await call<{ ok: true; item: DissertativeItem }>(
        `/api/manager/banks/dissertative-item?id=${encodeURIComponent(bankId)}&idx=${idx}&action=regenerate`,
        { method: "POST" }
      );
      onUpdated(r.item);
    } catch (e) {
      alert(`Falha: ${friendlyError(e)}`);
    } finally {
      setRegenerating(false);
    }
  }

  async function remove() {
    if (!confirm(`Remover a questão dissertativa ${idx + 1}? Outros itens reordenam.`)) return;
    setRemoving(true);
    try {
      await call(
        `/api/manager/banks/dissertative-item?id=${encodeURIComponent(bankId)}&idx=${idx}`,
        { method: "DELETE" }
      );
      onRemoved();
    } catch (e) {
      alert(`Falha: ${friendlyError(e)}`);
      setRemoving(false);
    }
  }

  return (
    <article class="rounded-md border border-border bg-card/60">
      <header class="flex items-center gap-2 border-b border-border px-3 py-2">
        <span class="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-fuchsia-500/15 text-[11px] font-bold text-fuchsia-700 dark:text-fuchsia-300">
          {idx + 1}
        </span>
        <span class="min-w-0 flex-1 truncate text-sm font-semibold">
          {item.tema || "Sem tema"}
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
          title={collapsed ? "Expandir" : "Recolher"}
        >
          {collapsed ? "Ver detalhes" : "Recolher"}
        </button>
        <button
          type="button"
          onClick={() => setEditing(!editing)}
          disabled={regenerating || removing}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
        >
          <Pencil width="0.9em" height="0.9em" />
          {editing ? "Cancelar edição" : "Editar"}
        </button>
        <button
          type="button"
          onClick={() => void regenerate()}
          disabled={regenerating || removing || !hasMc}
          title={hasMc ? "Refaz pela IA" : "Sem MC original — regenerar indisponível"}
          class="inline-flex items-center gap-1 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-1 text-[11px] font-semibold text-fuchsia-700 hover:bg-fuchsia-500/20 disabled:opacity-50 dark:text-fuchsia-300"
        >
          {regenerating ? (
            <Loader2 width="0.9em" height="0.9em" className="animate-spin" />
          ) : (
            <RefreshCw width="0.9em" height="0.9em" />
          )}
          IA refaz
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={regenerating || removing}
          class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-[11px] font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
          title="Remover essa questão dissertativa"
        >
          {removing ? (
            <Loader2 width="0.9em" height="0.9em" className="animate-spin" />
          ) : (
            <Trash2 width="0.9em" height="0.9em" />
          )}
        </button>
      </header>

      {(!collapsed || editing) && (
        <div class="space-y-3 p-3">
          {editing ? (
            <EditForm
              bankId={bankId}
              idx={idx}
              item={item}
              onCancel={() => setEditing(false)}
              onSaved={(next) => {
                onUpdated(next);
                setEditing(false);
              }}
            />
          ) : (
            <ViewOnly item={item} />
          )}
        </div>
      )}
    </article>
  );
}

function ViewOnly({ item }: { item: DissertativeItem }) {
  return (
    <div class="space-y-3 text-xs">
      <Section title="Enunciado">
        <p class="whitespace-pre-wrap">{item.enunciado}</p>
      </Section>
      {item.caso && item.caso.trim() && (
        <Section title="Caso (herdado do MC)">
          <p class="whitespace-pre-wrap text-muted-foreground">{item.caso}</p>
        </Section>
      )}
      <Section title="Resposta-modelo">
        <p class="whitespace-pre-wrap">{item.gabarito.resposta_modelo}</p>
      </Section>
      {item.gabarito.pontos_esperados.length > 0 && (
        <Section title={`Pontos esperados (${item.gabarito.pontos_esperados.length})`}>
          <ul class="space-y-1">
            {item.gabarito.pontos_esperados.map((p, i) => (
              <li key={i} class="flex items-start gap-2">
                <span class="inline-flex shrink-0 items-center justify-center rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[9px] font-bold text-violet-700 dark:text-violet-300">
                  {p.peso}
                </span>
                <span class="flex-1">{p.ponto}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {item.gabarito.criterios.length > 0 && (
        <Section title="Critérios qualitativos">
          <ul class="list-disc space-y-0.5 pl-5 text-muted-foreground">
            {item.gabarito.criterios.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </Section>
      )}
      {item.gabarito.armadilhas_comuns.length > 0 && (
        <Section title="Armadilhas comuns">
          <ul class="list-disc space-y-0.5 pl-5 text-amber-800 dark:text-amber-200">
            {item.gabarito.armadilhas_comuns.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  children
}: {
  title: string;
  children: preact.ComponentChildren;
}) {
  return (
    <div>
      <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div class="rounded-md border border-border bg-background/40 px-3 py-2">{children}</div>
    </div>
  );
}

function EditForm({
  bankId,
  idx,
  item,
  onCancel,
  onSaved
}: {
  bankId: string;
  idx: number;
  item: DissertativeItem;
  onCancel: () => void;
  onSaved: (next: DissertativeItem) => void;
}) {
  const [enunciado, setEnunciado] = useState(item.enunciado);
  const [respostaModelo, setRespostaModelo] = useState(item.gabarito.resposta_modelo);
  const [tema, setTema] = useState(item.tema);
  const [pontosText, setPontosText] = useState(
    item.gabarito.pontos_esperados
      .map((p) => `[${p.peso}] ${p.ponto}`)
      .join("\n")
  );
  const [criteriosText, setCriteriosText] = useState(item.gabarito.criterios.join("\n"));
  const [armadilhasText, setArmadilhasText] = useState(
    item.gabarito.armadilhas_comuns.join("\n")
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function parsePontos(): Array<{ ponto: string; peso: number }> {
    return pontosText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        // formato esperado: "[N] texto"
        const m = /^\[(\d+)\]\s*(.*)$/.exec(l);
        if (m) {
          const peso = Math.max(1, Math.min(3, parseInt(m[1], 10)));
          return { ponto: m[2].trim(), peso };
        }
        return { ponto: l, peso: 1 };
      })
      .filter((p) => p.ponto);
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const r = await call<{ ok: true; item: DissertativeItem }>(
        `/api/manager/banks/dissertative-item?id=${encodeURIComponent(bankId)}&idx=${idx}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            enunciado: enunciado.trim(),
            tema: tema.trim(),
            gabarito: {
              resposta_modelo: respostaModelo,
              pontos_esperados: parsePontos(),
              criterios: criteriosText.split("\n").map((s) => s.trim()).filter(Boolean),
              armadilhas_comuns: armadilhasText
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean)
            }
          })
        }
      );
      onSaved(r.item);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="space-y-3 text-xs">
      <Field label="Tema">
        <input
          value={tema}
          onInput={(e) => setTema((e.target as HTMLInputElement).value)}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      </Field>
      <Field label="Enunciado">
        <textarea
          value={enunciado}
          onInput={(e) => setEnunciado((e.target as HTMLTextAreaElement).value)}
          rows={3}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      </Field>
      <Field label="Resposta-modelo">
        <textarea
          value={respostaModelo}
          onInput={(e) => setRespostaModelo((e.target as HTMLTextAreaElement).value)}
          rows={6}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      </Field>
      <Field label="Pontos esperados (uma por linha, formato [PESO] texto)">
        <textarea
          value={pontosText}
          onInput={(e) => setPontosText((e.target as HTMLTextAreaElement).value)}
          rows={5}
          placeholder="[2] Definir conceito X&#10;[1] Mencionar exemplo Y"
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
        />
      </Field>
      <Field label="Critérios qualitativos (uma por linha)">
        <textarea
          value={criteriosText}
          onInput={(e) => setCriteriosText((e.target as HTMLTextAreaElement).value)}
          rows={3}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      </Field>
      <Field label="Armadilhas comuns (uma por linha)">
        <textarea
          value={armadilhasText}
          onInput={(e) => setArmadilhasText((e.target as HTMLTextAreaElement).value)}
          rows={3}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      </Field>
      {err && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
          <AlertCircle className="mr-1 inline size-3" />
          {err}
        </div>
      )}
      <div class="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-[11px] hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 width="0.9em" height="0.9em" className="animate-spin" />
          ) : (
            <Save width="0.9em" height="0.9em" />
          )}
          Salvar
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children
}: {
  label: string;
  children: preact.ComponentChildren;
}) {
  return (
    <label class="block">
      <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
